import { useEffect, useState } from "react";
import { Link } from "react-router";
import {
  ArrowLeft,
  Loader2,
  ExternalLink,
  Star,
  X,
  FileText,
  BadgeCheck,
  Search,
  Lock,
  Package,
  History,
  Users,
} from "lucide-react";
import { toast } from "sonner";

import { useContracting } from "@/hooks/queries/useContractings";
import { useSuretyMode } from "@/hooks/useSuretyMode";
import { useSuretyClients } from "@/hooks/queries/useSuretyClients";
import { useFollowedCompanies } from "@/hooks/queries/useFollowedCompanies";
import {
  useUpdateContractingStatus,
  useTogglePriceItem,
} from "@/hooks/mutations/useContractingMutations";
import { useUnlockContracting } from "@/hooks/mutations/useUnlockContracting";
import { useViewQuota } from "@/hooks/queries/useViewQuota";
import { StatusActions } from "./StatusActions";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StarButton } from "@/components/ui/StarButton";
import { PdfViewerModal } from "./PdfViewerModal";
import { BidRequirementsTab } from "./BidRequirementsTab";
import { cn } from "@/lib/utils";
import { formatDate, formatMoney, displayedAmount } from "@/lib/format";
import { subjectLabel, SectionTitle, DocCard, Meta } from "./detail-parts";
import { NewBadge, MinorBadge, StagePill, FROM_BASES_HINT } from "./pills";
import { TeamCard } from "@/components/team/TeamCard";
import { ContractingTimeline } from "./ContractingTimeline";
import { ScheduleActionsCard } from "./ScheduleActionsCard";
import { NotesCard } from "./NotesCard";
import { TasksCard } from "./TasksCard";
import { useContractingDetailModel } from "./useContractingDetailModel";
import { QualificationStateNote } from "./QualificationStateNote";
import {
  AwardCardWorks,
  BiddersList,
  WinnerItemsSection,
  WinnerContactsCard,
} from "./AwardDetails";

export type DetailTab = "detalle" | "requisitos";

export function ContractingDetailContent({
  contractingId,
  searchProfileId,
  locked,
  analyzeOnUnlock = false,
  onLockedChange,
  onClose,
  onBack,
  initialTab = "detalle",
  hideFullScreen = false,
}: {
  contractingId: number;
  searchProfileId?: number;
  /** Pista del llamador: la fila/ítem/nodo de origen ya sabe que está bloqueada. */
  locked?: boolean;
  /** Desbloquearla también la cruza contra los documentos: el botón lo dice. */
  analyzeOnUnlock?: boolean;
  onLockedChange?: (locked: boolean) => void;
  onClose?: () => void;
  /** Solo en la página completa: botón de volver dentro del encabezado. */
  onBack?: () => void;
  /** La pestaña con la que abre; sin bases leídas, Requisitos no existe y abre en Detalle. */
  initialTab?: DetailTab;
  /** Sin el enlace a pantalla completa: el onboarding no deja salir a la página. */
  hideFullScreen?: boolean;
}) {
  // Desbloqueo hecho desde este panel: el llamador todavía puede tener su fila
  // redactada en caché, así que mandamos nosotros hasta que llegue la data real.
  const [unlockedHere, setUnlockedHere] = useState(false);
  useEffect(() => setUnlockedHere(false), [contractingId]);

  const skipFetch = locked === true && !unlockedHere;
  const { data, isLoading, isError, error } = useContracting(
    contractingId,
    searchProfileId,
    { enabled: !skipFetch },
  );
  const unlockMutation = useUnlockContracting();
  const { data: quota } = useViewQuota();
  const statusMutation = useUpdateContractingStatus();
  const togglePriceItem = useTogglePriceItem();
  const [priceSearch, setPriceSearch] = useState("");
  const [pdf, setPdf] = useState<{
    url: string;
    title: string;
    page?: number;
  } | null>(null);

  const c: any = data;

  const isSurety = useSuretyMode();

  const {
    sourceFiles,
    documents,
    schedule,
    procedureActions,
    scheduleUpdatedAt,
    priceItems,
    activeProfileName,
    matchedItems,
    winnerGroups,
    unassignedItems,
    bidders,
    bidRequirements,
    bidAnalysis,
  } = useContractingDetailModel(c, searchProfileId);

  // Entre los postores se marcan las empresas conocidas: en Fianzas la cartera de
  // toda la organización, en el resto las que sigue el usuario.
  const hasBidders = !!bidders?.length;
  const { data: suretyClients } = useSuretyClients(isSurety && hasBidders);
  const followed = useFollowedCompanies(!isSurety && hasBidders);
  const knownBidderRucs = new Set(
    isSurety
      ? suretyClients?.map((client) => client.ruc)
      : followed.companies.map((company) => company.ruc),
  );

  // Oportunidad bloqueada: requiere gastar una vista para ver el detalle.
  // Se evalúa antes que el loader: en navegación directa el detalle responde
  // 403 (sin data), y desde el listado el placeholder llega redactado (isLocked).
  const isLocked =
    !unlockedHere &&
    (skipFetch ||
      (error as any)?.errorCode === "LOCKED_CONTRACTING" ||
      (c as any)?.isLocked === true);

  // El contenedor (modal) se dimensiona según esto: compacto mientras bloquea.
  useEffect(() => {
    onLockedChange?.(isLocked);
  }, [isLocked, onLockedChange]);

  if (isLocked) {
    return (
      <div className="flex flex-col items-center gap-4 p-8 text-center">
        <span className="flex size-14 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Lock className="size-7" />
        </span>
        <div>
          <p className="text-base font-bold">Oportunidad bloqueada</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Desbloquea esta licitación para ver su nomenclatura, descripción y
            todos los detalles. Se descontará 1 vista de tu plan y quedará
            desbloqueada para siempre.
            {analyzeOnUnlock &&
              " Además analizaremos sus requisitos con tus documentos."}
          </p>
          {quota && !quota.unlimited && (
            <p className="mt-2 text-sm text-muted-foreground">
              Te quedan <strong>{quota.remaining}</strong> vistas este mes.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {(onClose ?? onBack) && (
            <Button variant="outline" onClick={onClose ?? onBack}>
              {onClose ? "Cancelar" : "Volver"}
            </Button>
          )}
          <Button
            onClick={() =>
              unlockMutation.mutate(
                { contracting_id: contractingId },
                { onSuccess: () => setUnlockedHere(true) },
              )
            }
            disabled={unlockMutation.isPending}
          >
            <Lock className="mr-1.5 size-4" />
            {unlockMutation.isPending
              ? "Desbloqueando…"
              : analyzeOnUnlock
                ? "Desbloquear y analizar requisitos · 1 vista"
                : "Desbloquear · 1 vista"}
          </Button>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 py-20">
        <p className="text-center text-sm text-muted-foreground">
          No pudimos cargar el detalle de esta licitación.
        </p>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            Volver
          </Button>
        )}
      </div>
    );
  }

  if (isLoading || !c) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3">
        <Loader2 className="size-8 animate-spin text-primary" />
        <p className="text-sm font-semibold text-muted-foreground">
          Cargando detalles del proceso...
        </p>
      </div>
    );
  }

  const cu = c.contractingusers?.[0] ?? c.contractinguser ?? null;
  const status = cu?.contractinguser_status ?? null;
  const saved = cu != null && status !== null && status !== undefined;
  const currencySymbol = c.currency?.currency_symbol || "S/";

  // Obras tienen relación de precios y suelen ser 1 ítem → los ítems se fusionan
  // en la card de Buena Pro. Bien/servicio/consultoría no: los ítems por ganador
  // son el protagonista y no hay tab de relación de precios.
  const isWorks = c.contracting_contractsubject?.toLowerCase() === "works";

  const filteredPriceItems = priceSearch.trim()
    ? priceItems.filter((i) =>
        i.description?.toLowerCase().includes(priceSearch.trim().toLowerCase()),
      )
    : priceItems;

  // Items the user manually saved as interests (only relevant without an
  // active search profile, mirroring the legacy app).
  const savedPriceItems = priceItems.filter((i) => i.isSaved);

  // Saved state by price-item id, so the matched-interests section can show
  // and toggle the follow star without leaving the modal.
  const savedById = new Map<number, boolean>(
    priceItems.map((i) => [i.id, i.isSaved]),
  );

  function toggleItemFollow(priceitemId: number | null) {
    if (priceitemId == null) return;
    togglePriceItem.mutate(
      { contracting_id: contractingId, priceitem_id: priceitemId },
      { onError: () => toast.error("No se pudo actualizar el ítem") },
    );
  }

  // Qué monto se pinta y si salió de las bases, igual que en la fila y la tarjeta.
  const detailAmount = displayedAmount(c);

  // Días que quedan para inscribirse. Es lo único de la cabecera que caduca, así
  // que se pinta en rojo cuando aprieta — mismo umbral que el tablero.
  const registrationEndsAt = c.registrationEndsAt
    ? new Date(c.registrationEndsAt)
    : null;
  const registrationDaysLeft = registrationEndsAt
    ? Math.ceil((registrationEndsAt.getTime() - Date.now()) / 86_400_000)
    : null;

  // Group items by category to visually separate the different item types.
  // Only render the group headers when real categories exist.
  const hasCategories = priceItems.some((i) => i.category);
  const priceGroups = (() => {
    const groups = new Map<string, any[]>();
    for (const it of filteredPriceItems) {
      const key = it.category ?? "Sin categoría";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(it);
    }
    return Array.from(groups.entries())
      .map(([category, items]) => ({ category, items }))
      .sort((a, b) => a.category.localeCompare(b.category, "es"));
  })();

  function toggleSave() {
    statusMutation.mutate(
      { contracting_id: contractingId, status: saved ? null : 0 },
      { onError: () => toast.error("No se pudo actualizar la licitación") },
    );
  }

  return (
    <div className="flex h-full max-h-[90vh] flex-col">
      {/* Header */}
      <div className="flex shrink-0 flex-row gap-4 bg-slate-50 px-8 pb-4 pt-5 dark:bg-slate-900/10 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-col">
          <div className="mb-1.5 flex items-center gap-2">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                title="Volver"
                className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full border bg-card text-slate-500 transition-colors hover:bg-accent/30 hover:text-slate-700 active:scale-90 dark:hover:text-slate-200"
              >
                <ArrowLeft className="size-4" />
                <span className="sr-only">Volver</span>
              </button>
            )}
            <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider text-primary">
              {subjectLabel(c.contracting_contractsubject)}
            </span>
            {c.contractingversion?.[0]?.contractingstage?.contractingstage_name && (
              <StagePill
                stage={c.contractingversion[0].contractingstage.contractingstage_name}
                className="px-2.5 text-xs"
              />
            )}
            {c.isNew && <NewBadge className="text-xs" />}
            {c.contracting_minorid && <MinorBadge className="text-xs" />}
          </div>
          <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
            <h2
              className="max-w-xl truncate text-2xl font-extrabold leading-tight tracking-tight text-slate-800 dark:text-slate-100"
              title={c.contracting_nomenclature}
            >
              {c.contracting_nomenclature}
            </h2>
            <span className="text-[13px] font-normal text-slate-500">
              {formatMoney(detailAmount.amount, currencySymbol)}
              {detailAmount.fromBases && (
                <span title={FROM_BASES_HINT} className="cursor-help">
                  {" "}
                  · bases
                </span>
              )}
              {registrationEndsAt && (
                <>
                  {" · registro hasta "}
                  <span
                    className={cn(
                      "font-semibold",
                      registrationDaysLeft !== null && registrationDaysLeft <= 3
                        ? "text-red-600 dark:text-red-400"
                        : "text-slate-600 dark:text-slate-300",
                    )}
                  >
                    {formatDate(c.registrationEndsAt)}
                  </span>
                </>
              )}
            </span>
          </div>
          <div className="mt-0.5 max-w-lg truncate text-[11.5px] font-bold text-slate-500">
            {c.contracting_entity}
          </div>
          <QualificationStateNote state={c.qualificationState} />
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 sm:ml-auto">
          {/* Pantalla completa y cerrar son del panel lateral: en la página ya se
              está en pantalla completa, y para salir está "Volver". */}
          {onClose && !hideFullScreen && (
            <Link
              to={`/contracting/${contractingId}`}
              onClick={onClose}
              title="Abrir en pantalla completa"
              className="mr-2 flex items-center gap-1 text-xs font-bold text-primary transition-colors hover:underline"
            >
              <ExternalLink className="size-4" />
              <span className="hidden md:inline">Pantalla completa</span>
            </Link>
          )}

          <StarButton
            saved={saved}
            onClick={toggleSave}
            disabled={statusMutation.isPending}
            title={saved ? "Quitar del CRM" : "Guardar en el CRM"}
          />

          {saved && (
            <StatusActions
              contractingId={contractingId}
              status={status}
              size="md"
            />
          )}

          {onClose && (
            <button
              onClick={onClose}
              className="flex size-8 cursor-pointer items-center justify-center rounded-full border bg-card text-slate-400 transition-colors hover:bg-accent/30 hover:text-slate-600 active:scale-90 dark:hover:text-slate-200"
            >
              <X className="size-4" />
              <span className="sr-only">Cerrar</span>
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        defaultValue={initialTab === "requisitos" && bidRequirements ? "requisitos" : "detalle"}
        className="flex min-h-0 flex-1 flex-col gap-0"
      >
        <TabsList
          variant="line"
          // En móvil las pestañas no caben: se desplazan de lado, sin barra visible.
          // `shrink-0` porque con el overflow el modal la aplastaba para caber en 90vh.
          className="h-auto w-full shrink-0 justify-start gap-7 overflow-x-auto overflow-y-hidden border-b bg-slate-50 px-8 [scrollbar-width:none] dark:bg-slate-900/10 group-data-[orientation=horizontal]/tabs:h-12 [&::-webkit-scrollbar]:hidden"
        >
          <TabsTrigger
            value="detalle"
            className="group/tab flex-none px-0 py-4 text-sm font-medium cursor-pointer text-slate-500 data-[state=active]:text-primary group-data-[variant=line]/tabs-list:data-[state=active]:after:bg-primary group-data-[orientation=horizontal]/tabs:after:bottom-[-4px]"
          >
            Detalle
          </TabsTrigger>
          {/* Fianzas no ve recursos: el backend no le manda los ítems de la relación de precios. */}
          {isWorks && !isSurety && (
            <TabsTrigger
              value="precios"
              className="group/tab flex-none px-0 py-4 text-sm font-medium cursor-pointer text-slate-500 data-[state=active]:text-primary group-data-[variant=line]/tabs-list:data-[state=active]:after:bg-primary group-data-[orientation=horizontal]/tabs:after:bottom-[-4px]"
            >
              Relación de Precios
              {priceItems.length > 0 && (
                <span className="rounded-full bg-slate-200/70 px-2 py-0.5 text-xs font-bold tabular-nums text-slate-500 group-data-[state=active]/tab:bg-primary/10 group-data-[state=active]/tab:text-primary dark:bg-slate-800 dark:text-slate-400">
                  {priceItems.length}
                </span>
              )}
            </TabsTrigger>
          )}
          {bidRequirements && (
            <TabsTrigger
              value="requisitos"
              className="group/tab flex-none px-0 py-4 text-sm font-medium cursor-pointer text-slate-500 data-[state=active]:text-primary group-data-[variant=line]/tabs-list:data-[state=active]:after:bg-primary group-data-[orientation=horizontal]/tabs:after:bottom-[-4px]"
            >
              Requisitos
              {bidRequirements.requirements.length > 0 && (
                <span className="rounded-full bg-slate-200/70 px-2 py-0.5 text-xs font-bold tabular-nums text-slate-500 group-data-[state=active]/tab:bg-primary/10 group-data-[state=active]/tab:text-primary dark:bg-slate-800 dark:text-slate-400">
                  {bidRequirements.requirements.length}
                </span>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        {/* TAB: Detalle */}
        <TabsContent
          value="detalle"
          className="flex-1 overflow-y-auto p-6 data-[state=inactive]:hidden"
        >
          <div className="flex flex-col gap-6 lg:flex-row">
            {/* Left column */}
            <div className="flex flex-1 flex-col gap-6 lg:w-2/3">
              {/* Metadata */}
              <div className="flex flex-col gap-4 rounded-2xl border border-slate-100 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/20">
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                  <Meta
                    label={
                      detailAmount.fromBases
                        ? "Monto Referencial · bases"
                        : "Monto Referencial"
                    }
                  >
                    {formatMoney(detailAmount.amount, currencySymbol)}
                  </Meta>
                  <Meta label="Departamento">
                    {c.department?.department_name || "No especificado"}
                  </Meta>
                  <Meta label="Publicación">
                    {formatDate(c.contracting_publicatedat)}
                  </Meta>
                </div>
                {c.contracting_description && (
                  <div className="border-t border-slate-200 pt-3 dark:border-slate-800">
                    <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                      Descripción / Objeto del Proceso
                    </div>
                    <p className="whitespace-pre-line text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                      {c.contracting_description}
                    </p>
                  </div>
                )}
              </div>

              <ScheduleActionsCard
                schedule={schedule}
                actions={procedureActions}
                scheduleUpdatedAt={scheduleUpdatedAt}
              />

              {/* Ítems en seguimiento. Salen de la relación de precios: en Fianzas no van. */}
              {!isSurety && savedPriceItems.length > 0 && (
                <div className="flex flex-col">
                  <SectionTitle
                    icon={
                      <Star className="size-4 fill-amber-400 text-amber-500" />
                    }
                  >
                    Ítems en seguimiento ({savedPriceItems.length})
                  </SectionTitle>
                  <div className="mt-3 flex flex-col gap-2">
                    {savedPriceItems.map((item, i) => (
                      <div
                        key={`${item.id}-${i}`}
                        className="flex items-center justify-between gap-3 rounded-xl border bg-amber-50/40 px-4 py-3 dark:bg-amber-900/10"
                      >
                        <div className="flex min-w-0 flex-col">
                          <span
                            className="truncate text-sm font-semibold text-slate-700 dark:text-slate-300"
                            title={item.description}
                          >
                            {item.description}
                          </span>
                          <span className="mt-0.5 text-xs text-slate-400 dark:text-slate-500">
                            {item.savedKeyword ? (
                              <>
                                Guardado por:{" "}
                                <strong className="font-bold uppercase text-slate-500 dark:text-slate-400">
                                  {item.savedKeyword}
                                </strong>
                              </>
                            ) : (
                              "Agregado manualmente"
                            )}
                          </span>
                        </div>
                        <div className="shrink-0 text-right">
                          <div className="text-xs text-slate-400">
                            Cant. {item.quantity} {item.unit}
                          </div>
                          <div className="text-sm font-bold text-slate-700 dark:text-slate-200">
                            {formatMoney(item.totalPrice, currencySymbol)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Intereses Coincidentes (lista plana, deduplicada) */}
              {!isSurety && matchedItems.length > 0 && (
                <div className="flex flex-col">
                  <SectionTitle
                    icon={<BadgeCheck className="size-4 text-blue-500" />}
                  >
                    Intereses Coincidentes · {matchedItems.length}{" "}
                    {matchedItems.length === 1 ? "ítem" : "ítems"}
                  </SectionTitle>
                  <div className="mt-3 flex flex-col gap-1.5">
                    {matchedItems.map((item, idx) => {
                      const isFollowed =
                        item.priceitemId != null
                          ? (savedById.get(item.priceitemId) ?? false)
                          : false;
                      return (
                        <div
                          key={item.priceitemId ?? idx}
                          className="flex items-center justify-between gap-3 rounded-xl border bg-slate-50/40 px-4 py-3 dark:border-slate-800/80 dark:bg-slate-900/10"
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            <div className="flex min-w-0 flex-col">
                              <span
                                className="truncate text-sm font-medium text-slate-700 dark:text-slate-300"
                                title={item.description}
                              >
                                {item.description}
                              </span>
                              <div className="mt-1 flex flex-wrap items-center gap-1">
                                {[...item.interests].map((kw) => (
                                  <span
                                    key={`k-${kw}`}
                                    className="rounded-full border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[9px] font-bold text-blue-700 dark:border-blue-900/50 dark:bg-blue-950/20 dark:text-blue-300"
                                  >
                                    {kw}
                                  </span>
                                ))}
                                {[...item.profiles].map((pname) => {
                                  const active = pname === activeProfileName;
                                  return (
                                    <span
                                      key={`p-${pname}`}
                                      className={cn(
                                        "rounded-full px-1.5 py-0.5 text-[9px] font-semibold",
                                        active
                                          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                                          : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
                                      )}
                                    >
                                      {pname}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                          <div className="shrink-0 flex gap-2">
                            <div className="text-right">
                              {item.qty && (
                                <div className="text-xs text-slate-400">
                                  Cant. {item.qty}
                                </div>
                              )}
                              {item.total && (
                                <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                                  {item.total}
                                </div>
                              )}
                            </div>

                            {item.priceitemId != null && (
                              <StarButton
                                saved={isFollowed}
                                disabled={togglePriceItem.isPending}
                                onClick={() =>
                                  toggleItemFollow(item.priceitemId!)
                                }
                                title={
                                  isFollowed
                                    ? "Quitar de mis intereses"
                                    : "Seguir este ítem"
                                }
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Bien/servicio: los ítems por ganador son el protagonista
                  (las obras los muestran fusionados en la card de Buena Pro). */}
              {!isWorks &&
                (winnerGroups.length > 0 || unassignedItems.length > 0) && (
                  <div className="flex flex-col">
                    <SectionTitle
                      icon={<Package className="size-4 text-slate-400" />}
                    >
                      Ítems Adjudicados por Ganador
                    </SectionTitle>
                    <div className="mt-3">
                      <WinnerItemsSection
                        winnerGroups={winnerGroups}
                        unassignedItems={unassignedItems}
                        currencySymbol={currencySymbol}
                      />
                    </div>
                  </div>
                )}

              {/* Documentos: título fuera de la tarjeta, como el cronograma */}
              <div className="flex flex-col">
                <SectionTitle
                  icon={<FileText className="size-4 text-slate-400" />}
                >
                  Documentos del Proceso
                </SectionTitle>
                <div className="mt-3 rounded-2xl border bg-card p-5">
                  {documents.length === 0 ? (
                    <div className="py-2 text-center text-xs italic text-slate-400">
                      No se encontraron documentos registrados para esta
                      licitación.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2">
                      {documents.map((doc, i) => (
                        <DocCard
                          key={i}
                          doc={doc}
                          onPreview={(url, title) => setPdf({ url, title })}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right column */}
            <div className="flex w-full flex-col gap-6 lg:w-1/3">
              {/* Buena Pro */}
              <div className="flex flex-col">
                <SectionTitle
                  icon={<BadgeCheck className="size-4 text-emerald-500" />}
                >
                  Buena Pro / Adjudicación
                </SectionTitle>
                <div className="mt-3 flex flex-col rounded-2xl border bg-card p-5">
                  {isWorks ? (
                    <AwardCardWorks
                      winnerGroups={winnerGroups}
                      unassignedItems={unassignedItems}
                      currencySymbol={currencySymbol}
                    />
                  ) : (
                    <WinnerContactsCard winnerGroups={winnerGroups} />
                  )}
                </div>
              </div>

              {bidders && (
                <div className="flex flex-col">
                  <SectionTitle icon={<Users className="size-4 text-slate-500" />}>
                    Postores
                  </SectionTitle>
                  <div className="mt-3 flex flex-col rounded-2xl border bg-card p-5">
                    <BiddersList
                      bidders={bidders}
                      knownRucs={knownBidderRucs}
                      knownLabel={isSurety ? "Tu cliente" : "La sigues"}
                    />
                  </div>
                </div>
              )}

              {/* Responsable: quién de la empresa la lleva. */}
              <TeamCard
                contractingId={contractingId}
                assignedUserId={cu?.assignedUserId ?? null}
              />

              {/* Tareas: el próximo paso, con fecha y dueño. */}
              <TasksCard contractingId={contractingId} />

              {/* Comentarios del equipo */}
              <NotesCard contractingId={contractingId} />

              {/* Historial: qué hizo cada quien con esta oportunidad. */}
              <div className="flex flex-col">
                <SectionTitle
                  icon={<History className="size-4 text-slate-400" />}
                >
                  Historial
                </SectionTitle>
                <div className="mt-3 rounded-2xl border bg-card p-5">
                  <ContractingTimeline contractingId={contractingId} />
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* TAB: Relación de Precios (solo obras) */}
        {isWorks && (
          <TabsContent
            value="precios"
            className="flex-1 overflow-y-auto px-6 pb-6 data-[state=inactive]:hidden"
          >
            {/* Documento(s) de origen */}
            {sourceFiles.length > 0 && (
              <div className="mb-5 mt-6 flex flex-col gap-3.5 rounded-2xl border bg-card p-4">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                  Documento(s) de Origen (Relación de Precios)
                </div>
                <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2">
                  {sourceFiles.map((doc: any, i: number) => (
                    <DocCard
                      key={i}
                      doc={doc}
                      proxyPreview={false}
                      onPreview={(url, title) => setPdf({ url, title })}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Título y buscador quedan fijos al bajar por la tabla, como en Requisitos. */}
            <div className="sticky top-0 z-20 -mx-6 mb-4 flex flex-col items-start justify-between gap-3 border-b bg-card px-6 pb-4 pt-6 sm:flex-row sm:items-center">
              <div>
                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">
                  Bienes y Servicios Requeridos
                </h3>
                <p className="mt-0.5 text-sm font-semibold text-slate-400">
                  Listado detallado de ítems extraídos del archivo técnico de la
                  licitación.
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-bold text-slate-500 dark:bg-slate-800">
                  {filteredPriceItems.length}
                  {priceSearch ? ` de ${priceItems.length}` : ""} ítems
                </span>
                {priceItems.length > 0 && (
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={priceSearch}
                      onChange={(e) => setPriceSearch(e.target.value)}
                      placeholder="Buscar ítem..."
                      className="h-9 w-44 pl-8"
                    />
                  </div>
                )}
              </div>
            </div>

            {filteredPriceItems.length === 0 ? (
              <div className="rounded-2xl border border-dashed bg-slate-50 p-12 text-center text-sm italic text-slate-400 dark:bg-slate-800/20">
                {priceItems.length === 0
                  ? "No se encontraron ítems para esta licitación."
                  : `No hay ítems que coincidan con "${priceSearch}".`}
              </div>
            ) : (
              <div className="overflow-x-auto rounded-2xl border">
                <table className="w-full border-collapse text-left text-xs">
                  <thead>
                    <tr className="border-b bg-slate-50 font-bold text-slate-500 dark:bg-slate-800/40">
                      <th className="p-3">Ítem / Descripción</th>
                      <th className="w-20 p-3 text-right">Cant.</th>
                      <th className="w-20 p-3">Unidad</th>
                      <th className="w-32 p-3 text-right">P. Unitario</th>
                      <th className="w-32 p-3 text-right">Total</th>
                      <th className="w-36 p-3 text-center">Coincidencia</th>
                      <th className="w-20 p-3 text-center">Guardado</th>
                    </tr>
                  </thead>
                  {priceGroups.map((group) => (
                    <tbody key={group.category}>
                      {hasCategories && (
                        <tr className="border-b bg-slate-100/70 dark:bg-slate-800/60">
                          <td
                            colSpan={7}
                            className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400"
                          >
                            {group.category}
                            <span className="ml-2 font-semibold text-slate-400">
                              ({group.items.length})
                            </span>
                          </td>
                        </tr>
                      )}
                      {group.items.map((it: any, i: number) => (
                        <tr
                          key={`${it.id}-${i}`}
                          className="border-b last:border-0 hover:bg-slate-50/60 dark:hover:bg-slate-800/20"
                        >
                          <td className="p-3 font-medium text-slate-700 dark:text-slate-300">
                            {it.description}
                          </td>
                          <td className="p-3 text-right tabular-nums">
                            {it.quantity}
                          </td>
                          <td className="p-3 text-slate-500">{it.unit}</td>
                          <td className="p-3 text-right font-mono tabular-nums">
                            {formatMoney(it.unitPrice, currencySymbol, true)}
                          </td>
                          <td className="p-3 text-right font-mono font-semibold tabular-nums">
                            {formatMoney(it.totalPrice, currencySymbol)}
                          </td>
                          <td className="p-3 text-center">
                            {it.hasMatch ? (
                              <span
                                title={
                                  it.matchedKeyword
                                    ? `Coincidencia con: ${it.matchedKeyword}`
                                    : "Coincidencia"
                                }
                                className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-blue-800 dark:bg-blue-900/40 dark:text-blue-200"
                              >
                                {it.matchedKeyword ?? "Match"}
                              </span>
                            ) : (
                              <span className="font-medium text-slate-300 dark:text-slate-700">
                                -
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            <StarButton
                              saved={it.isSaved}
                              disabled={togglePriceItem.isPending}
                              onClick={() => toggleItemFollow(it.id)}
                              title={
                                it.isSaved
                                  ? "Quitar de mis intereses"
                                  : "Marcar como de interés"
                              }
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  ))}
                </table>
              </div>
            )}
          </TabsContent>
        )}

        {bidRequirements && (
          <TabsContent
            value="requisitos"
            className="flex-1 overflow-y-auto px-6 pb-6 data-[state=inactive]:hidden"
          >
            <BidRequirementsTab
              contractingId={contractingId}
              data={bidRequirements}
              analysis={bidAnalysis}
              onOpenDocument={
                bidRequirements.url
                  ? (page) =>
                      setPdf({
                        url: bidRequirements.url!,
                        title: bidRequirements.fileName,
                        page,
                      })
                  : undefined
              }
            />
          </TabsContent>
        )}
      </Tabs>

      <PdfViewerModal
        initialPage={pdf?.page}
        url={pdf?.url ?? null}
        title={pdf?.title}
        open={pdf != null}
        onOpenChange={(o) => !o && setPdf(null)}
      />
    </div>
  );
}
